import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Favorite } from '@mui/icons-material'
import { MenuItem, Stack, TextField, Typography } from '@mui/material'

import { useGetQuartersQuery, useGetTopGamesWithAppIdsQuery } from '../../../../../api/top-grossing'
import { useGetInternalMarketsQuery } from '../../../../api/internal-markets'
import {
  filterRowsByName,
  getMarketShareAppIds,
  isMarketShareGameMissingSubgenre,
  marketShareGameFields,
  removeRowsWithStoredSubgenre,
  toTopGameRow,
} from '../../helpers/dashboardHelpers'
import { DashboardAction, DashboardActionHandler, DashboardColumn, StoredSubgenres } from '../../types/Dashboard'
import { DashboardGamesDialog } from '../DashboardGamesDialog/DashboardGamesDialog'
import { DashboardGamesTable } from '../DashboardGamesTable/DashboardGamesTable'

const columns = [DashboardColumn.Icon, DashboardColumn.Name, DashboardColumn.Publisher]
const actions = [DashboardAction.ViewAnalyses, DashboardAction.Edit]

type MarketShareGamesDialogProps = {
  open: boolean
  onClose: () => void
  onAction: DashboardActionHandler
  storedSubgenres: StoredSubgenres
}

export const MarketShareGamesDialog: FC<MarketShareGamesDialogProps> = ({ open, onClose, onAction, storedSubgenres }) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  // None at first, as legacy: a market's list reads every quarter's games.
  const [marketIso, setMarketIso] = useState('')
  useEffect(() => {
    setSearch('')
    setMarketIso('')
  }, [open])

  const marketsQuery = useGetInternalMarketsQuery(undefined, { skip: !open })
  const markets = useMemo(() => [...(marketsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [marketsQuery.data])
  const quartersQuery = useGetQuartersQuery({ marketIso }, { skip: !open || !marketIso })
  const appIds = useMemo(() => getMarketShareAppIds(quartersQuery.data ?? []), [quartersQuery.data])
  const gamesQuery = useGetTopGamesWithAppIdsQuery(
    { marketIso, appIds, include: marketShareGameFields.join(',') },
    { skip: !open || !marketIso || !quartersQuery.data || !appIds.length }
  )

  const rows = useMemo(
    () => removeRowsWithStoredSubgenre((gamesQuery.data ?? []).filter(isMarketShareGameMissingSubgenre).map(toTopGameRow), storedSubgenres),
    [gamesQuery.data, storedSubgenres]
  )
  const shownRows = useMemo(() => filterRowsByName(rows, search), [rows, search])
  const isLoading = quartersQuery.isFetching || gamesQuery.isFetching
  const isError = quartersQuery.isError || gamesQuery.isError

  const renderSummary = () => {
    if (!rows.length)
      return (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" py={2}>
          <Typography fontWeight="bold">{t('internal-analysts:dashboard_market_share_all_have_subgenre')}</Typography>
          <Favorite color="error" fontSize="small" />
        </Stack>
      )
    return <Typography>{t('internal-analysts:dashboard_market_share_missing_count', { count: rows.length })}</Typography>
  }

  return (
    <DashboardGamesDialog
      open={open}
      title={
        marketIso
          ? t('internal-analysts:dashboard_market_share_title_market', { market: marketIso.toUpperCase() })
          : t('internal-analysts:dashboard_market_share_title')
      }
      onClose={onClose}
      search={search}
      onSearchChange={setSearch}
      filters={
        <TextField
          select
          size="small"
          label={t('internal-common:market')}
          value={marketIso}
          onChange={(event) => setMarketIso(event.target.value)}
          error={marketsQuery.isError}
          helperText={marketsQuery.isError ? t('internal-analysts:games_markets_load_failed') : undefined}
          sx={{ minWidth: 220 }}
        >
          {markets.map((market) => (
            <MenuItem key={market.id} value={market.iso}>
              {market.name}
            </MenuItem>
          ))}
        </TextField>
      }
      isLoading={isLoading}
      error={isError ? t('internal-analysts:dashboard_list_failed') : undefined}
      summary={marketIso ? renderSummary() : undefined}
    >
      {marketIso ? (
        rows.length > 0 && (
          <DashboardGamesTable
            rows={shownRows}
            columns={columns}
            actions={actions}
            onAction={onAction}
            storedSubgenres={storedSubgenres}
            noRowsLabel={t('internal-analysts:dashboard_no_games')}
          />
        )
      ) : (
        <Typography color="text.secondary">{t('internal-analysts:dashboard_market_share_pick_market')}</Typography>
      )}
    </DashboardGamesDialog>
  )
}
